import { useState } from "react";
import PageContainer from "@/components/layout/page-container";
import { SignifyInterestForm } from "@/modules/opportunities/signify-interest-form";
import { SignifyInterestStepTwo } from "@/modules/opportunities/signify-interest-step-two";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetOpportunity,
  useApplyToOpportunity,
} from "@/services/opportunities/queries";
import { Skeleton } from "@/components/ui/skeleton";
import { z } from "zod";
import { showToast } from "@/components/ui/show-toast";

const signifyInterestSchema = z.object({
  contactName: z.string().min(1, "Contact person name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(1, "Phone number is required"),
});

type SignifyInterestFormData = z.infer<typeof signifyInterestSchema>;

export default function SignifyInterestPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [step, setStep] = useState<1 | 2>(1);
  const [stepOneData, setStepOneData] =
    useState<SignifyInterestFormData | null>(null);
  const MAX_FILE_SIZE_MB = 3;

  const { data: opportunityData, isLoading } = useGetOpportunity(id || "", {
    enabled: !!id,
  });

  const [isProcessing, setIsProcessing] = useState(false);

  const { mutate: applyToOpportunity, isPending: isSubmitting } =
    useApplyToOpportunity(
      () => {
        showToast("success", "Your interest has been successfully submitted!");
        navigate(`/dashboard/opportunities/${id}`);
      },
      (error: any) => {
        showToast(
          "error",
          error?.response?.data?.message ||
            "Failed to submit application. Please try again."
        );
        console.error("Application submission error:", error);
      }
    );

  const handleStepOneSubmit = (data: SignifyInterestFormData) => {
    setStepOneData(data);
    // If there are custom questions, go to step 2, otherwise submit
    if (
      opportunityData?.customQuestions &&
      opportunityData.customQuestions.length > 0
    ) {
      setStep(2);
    } else {
      handleFinalSubmit(data, {});
    }
  };

  const handleStepTwoSubmit = (stepTwoData: Record<string, any>) => {
    if (stepOneData) {
      handleFinalSubmit(stepOneData, stepTwoData);
    }
  };

  const handleFinalSubmit = async (
    stepOne: SignifyInterestFormData,
    stepTwo: Record<string, any>
  ) => {
    if (!id || !opportunityData) return;

    setIsProcessing(true);
    try {
      // Format custom responses (files are already uploaded as URLs)
      const customResponses = (opportunityData.customQuestions || [])
        .filter((question: any) => {
          // Only include questions that have a response value
          const responseValue = stepTwo[question.name];
          return (
            responseValue !== undefined &&
            responseValue !== null &&
            responseValue !== ""
          );
        })
        .map((question: any) => {
          const responseValue = stepTwo[question.name];

          const customResponse: any = {
            questionId: question.id || question._id || question.name,
            questionLabel: question.label,
          };

          if (question.type === "file") {
            // Files are already uploaded and stored as URLs
            if (
              typeof responseValue === "string" &&
              responseValue.trim() !== ""
            ) {
              // URL string format
              const fileName =
                responseValue.split("/").pop() || "uploaded-file";
              customResponse.files = [
                {
                  name: fileName,
                  url: responseValue,
                },
              ];
            } else if (
              typeof responseValue === "object" &&
              responseValue !== null &&
              "url" in responseValue
            ) {
              // Object format with url and name
              customResponse.files = [
                {
                  name: (responseValue as any).name || "uploaded-file",
                  url: (responseValue as any).url,
                },
              ];
            } else if (Array.isArray(responseValue)) {
              if (responseValue.length === 0) {
                return null;
              }
              if (typeof responseValue[0] === "string") {
                customResponse.files = responseValue
                  .filter((url: any) => typeof url === "string" && url.trim())
                  .map((url: string) => ({
                    name: url.split("/").pop() || "uploaded-file",
                    url,
                  }));
              } else {
                customResponse.files = responseValue
                  .filter(
                    (file: any) =>
                      file &&
                      typeof file === "object" &&
                      "url" in file &&
                      file.url
                  )
                  .map((file: any) => ({
                    name: file.name || "uploaded-file",
                    url: file.url,
                  }));
              }
              if (!customResponse.files || customResponse.files.length === 0) {
                return null;
              }
            } else {
              // If file type but no valid file data, skip this response
              return null;
            }
          } else {
            // For non-file types, use response directly
            if (
              responseValue !== undefined &&
              responseValue !== null &&
              responseValue !== ""
            ) {
              customResponse.response = responseValue;
            } else {
              // If no valid response, skip this response
              return null;
            }
          }

          return customResponse;
        });

      // Filter out any null responses or responses that don't have either files or response
      const validCustomResponses = customResponses.filter(
        (response: any) =>
          response !== null &&
          (response.files || response.response !== undefined)
      );

      setIsProcessing(false);

      // Submit application
      const payload = {
        contactPersonName: stepOne.contactName,
        contactPersonEmail: stepOne.email,
        contactPersonPhone: stepOne.phone,
        ...(validCustomResponses.length > 0 && {
          customResponses: validCustomResponses,
        }),
      };

      console.log("Submitting application with payload:", payload);

      applyToOpportunity({
        id,
        data: payload,
      });
    } catch (error: any) {
      setIsProcessing(false);
      showToast("error", "Failed to process application. Please try again.");
      console.error("Application processing error:", error);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else {
      navigate(-1);
    }
  };

  if (isLoading) {
    return (
      <PageContainer scrollable={true}>
        <div className="max-w-4xl rounded bg-white p-6">
          <div className="space-y-6">
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96 mb-8" />
            <div className="space-y-5">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!opportunityData) {
    return (
      <PageContainer scrollable={true}>
        <div className="max-w-4xl rounded bg-white p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-900">
              Opportunity not found
            </h2>
            <p className="text-gray-600 mt-2">
              The opportunity you're looking for doesn't exist.
            </p>
            <button
              onClick={() => navigate("/dashboard/opportunities")}
              className="mt-4 text-blue-600 hover:underline"
            >
              Back to Opportunities
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  const fileQuestions = (opportunityData.customQuestions || []).filter(
    (question: any) => question.type === "file"
  );
  const maxAllowedFiles = fileQuestions.reduce((max: number, question: any) => {
    const maxFiles = question.fileConfig?.maxFiles || 1;
    return Math.max(max, maxFiles);
  }, 1);
  const hasMultipleFileUploads = maxAllowedFiles > 1;

  return (
    <PageContainer scrollable={true}>
      <div className="max-w-4xl rounded bg-white p-6">
        <div className="space-y-6">
          {step === 1 ? (
            <SignifyInterestForm
              onSubmit={handleStepOneSubmit}
              onBack={handleBack}
            />
          ) : (
            <>
              {fileQuestions.length > 0 && (
                <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                  <p>Do not upload files larger than {MAX_FILE_SIZE_MB}MB.</p>
                  {hasMultipleFileUploads && (
                    <p>You can upload multiple files where allowed.</p>
                  )}
                </div>
              )}
              <SignifyInterestStepTwo
                customQuestions={opportunityData.customQuestions || []}
                onSubmit={handleStepTwoSubmit}
                onBack={handleBack}
              />
            </>
          )}
        </div>
      </div>
      {(isSubmitting || isProcessing) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <p className="text-sm font-medium">
              Processing your application...
            </p>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
